import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import * as authService from '../../services/authService'
import { pendingDepositDraftService, type PendingDepositDraft } from '../../services/pendingDepositDraftService'
import {
  getTransactionSlipPreviewUrl,
  getUserTransactions,
  type WalletTransaction,
} from '../../services/walletTransactionService'
import * as walletService from '../../services/walletService'
import { HistoryCollapseCard, type HistoryCardLeftIcon } from './HistoryCollapseCard'
import { translateDepositReviewReason } from '../../utils/depositReviewReason'

type WalletHistoryMode = 'deposit' | 'withdraw' | 'credit'

type HistoryItem = {
  id: string
  refId: string
  title: string
  subtitle: string
  amountText: string
  amountClassName: string
  leftIcon: HistoryCardLeftIcon
  rows: Array<{ label: string; value: string }>
  searchText: string
  sortAt: number
  queue?: 'completed' | 'pending'
  actionLabel?: string
  onAction?: () => void
  secondaryActionLabel?: string
  onSecondaryAction?: () => void
}

const TYPE_LABEL: Record<string, string> = {
  deposit: 'ฝากเครดิต',
  withdraw: 'ถอนเครดิต',
  bonus: 'โบนัส',
  transfer_in: 'รับโอนเข้า',
  transfer_out: 'โอนออก',
  refund: 'คืนเงิน',
  adjustment: 'ปรับยอด',
  fee: 'ค่าธรรมเนียม',
  bet: 'เดิมพัน',
  win: 'รับรางวัล',
  cancel: 'ยกเลิกรายการ',
}

const STATUS_LABEL: Record<string, string> = {
  draft: 'รอทำรายการต่อ',
  pending: 'รอดำเนินการ',
  processing: 'กำลังดำเนินการ',
  reserved: 'รอโอนเงิน',
  verified: 'อนุมัติ',
  completed: 'สำเร็จ',
  approved: 'สำเร็จ',
  failed: 'ไม่สำเร็จ',
  rejected: 'ปฏิเสธ',
  cancelled: 'ยกเลิก',
  expired: 'หมดอายุ',
  locked: 'รอตรวจสอบเพิ่มเติม',
}

function formatDateTime(dateStr?: string | null): string {
  if (!dateStr) return '-'
  try {
    return new Date(dateStr).toLocaleString('th-TH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateStr
  }
}

function formatMoney(value?: number | string | null): string {
  return walletService.formatCurrency(Number(value) || 0)
}

function getStatusLabel(status?: string): string {
  return STATUS_LABEL[String(status || '').toLowerCase()] || String(status || '-')
}

function getTransactionTypeLabel(type?: string): string {
  return TYPE_LABEL[String(type || '').toLowerCase()] || String(type || '-')
}

function getLeftIconFromStatus(status?: string): HistoryCardLeftIcon {
  const normalized = String(status || '').toLowerCase()
  if (['verified', 'completed', 'approved'].includes(normalized)) return 'success'
  if (['rejected', 'failed', 'cancelled', 'expired'].includes(normalized)) return 'reject'
  if (['draft', 'pending', 'processing', 'reserved', 'locked'].includes(normalized)) return 'pending'
  return 'info'
}

function getSignedAmountText(amount: number): string {
  if (amount > 0) return `+${formatMoney(amount)} บาท`
  if (amount < 0) return `-${formatMoney(Math.abs(amount))} บาท`
  return `${formatMoney(0)} บาท`
}

function normalizeWithdrawUserText(value?: string | null): string {
  const normalized = String(value || '').trim()
  if (!normalized) return ''

  const genericValues = new Set([
    '-',
    'bank_transfer',
    'user_withdrawal_request',
    'User withdrawal request',
  ])

  return genericValues.has(normalized) ? '' : normalized
}

function getWithdrawChannelLabel(tx: WalletTransaction): string {
  const category = String(tx.category || '').trim().toLowerCase()
  if (category === 'bank_transfer') return 'โอนเข้าบัญชีธนาคาร'
  return category ? category : 'ถอนเข้าบัญชีธนาคาร'
}

function getWithdrawNote(tx: WalletTransaction): string {
  const status = String(tx.status || '').trim().toLowerCase()
  const approvalStatus = String(tx.approval?.status || '').trim().toLowerCase()
  const explicitReason =
    normalizeWithdrawUserText(tx.approval?.rejectionReason)
    || normalizeWithdrawUserText(tx.metadata?.notes)
    || normalizeWithdrawUserText(tx.details?.description)

  if (status === 'completed' || status === 'approved') {
    return explicitReason || 'ระบบโอนเงินเข้าบัญชีของคุณเรียบร้อยแล้ว'
  }

  if (status === 'reserved') {
    return explicitReason || 'เจ้าหน้าที่รับรายการแล้ว และกำลังเตรียมโอนเงินให้คุณ'
  }

  if (status === 'processing') {
    return explicitReason || (
      approvalStatus === 'approved'
        ? 'รายการถอนของคุณได้รับการอนุมัติแล้ว และกำลังดำเนินการโอนเงิน'
        : 'รายการถอนของคุณกำลังอยู่ระหว่างตรวจสอบ'
    )
  }

  if (status === 'locked') {
    return explicitReason || 'รายการถอนของคุณกำลังรอตรวจสอบเพิ่มเติมจากเจ้าหน้าที่'
  }

  if (status === 'expired') {
    return explicitReason || 'รายการถอนหมดอายุ กรุณาส่งคำขอถอนใหม่อีกครั้ง'
  }

  if (['failed', 'rejected', 'cancelled'].includes(status)) {
    return explicitReason || 'รายการถอนยังไม่สำเร็จ กรุณาติดต่อเจ้าหน้าที่หากยอดเงินยังไม่กลับเข้าระบบ'
  }

  if (approvalStatus === 'pending') {
    return explicitReason || 'รายการถอนของคุณถูกส่งแล้ว และกำลังรอเจ้าหน้าที่อนุมัติ'
  }

  if (approvalStatus === 'approved') {
    return explicitReason || 'รายการถอนของคุณได้รับการอนุมัติแล้ว และกำลังรอการโอนเงิน'
  }

  return explicitReason || 'รายการถอนของคุณถูกส่งแล้ว และกำลังอยู่ระหว่างดำเนินการ'
}

function getDepositTargetAccount(tx: WalletTransaction): {
  bankName: string
  accountName: string
  accountNumber: string
} {
  return {
    bankName: String(tx.metadata?.selectedDepositBankName || tx.details?.bankAccount?.bankCode || '').trim() || 'รายการฝากเงิน',
    accountName: String(tx.metadata?.selectedDepositAccountName || tx.details?.bankAccount?.accountName || '').trim() || '-',
    accountNumber: String(tx.metadata?.selectedDepositAccountNumber || tx.details?.bankAccount?.accountNumber || '').trim() || '-',
  }
}

function getDepositChannelLabel(tx: WalletTransaction): string {
  const method = String(tx.metadata?.depositMethod || '').trim().toLowerCase()
  if (method === 'prompt_pay') return 'พร้อมเพย์'
  if (method === 'true_money') return 'TrueMoney'
  if (method === 'decimal_reservation') return 'Payment Gateway'
  const sourceChannel = String(tx.metadata?.depositRouting?.sourceChannel || '').trim().toLowerCase()
  if (sourceChannel === 'payment') return 'Payment Gateway'
  if (sourceChannel === 'slip') return 'โอนผ่านธนาคารพร้อมสลิป'
  return 'โอนผ่านธนาคาร'
}

function getSlipStatusLabel(tx: WalletTransaction): string {
  if (tx.metadata?.submittedSlip?.imageUrl || tx.slipVerification?.slipData?.imageUrl) {
    const slipStatus = String(tx.slipVerification?.status || '').trim().toLowerCase()
    if (slipStatus === 'verified') return 'แนบแล้วและตรวจสอบผ่าน'
    if (slipStatus === 'rejected') return 'แนบแล้วแต่ตรวจสอบไม่ผ่าน'
    if (slipStatus === 'submitted' || slipStatus === 'pending') return 'แนบแล้ว รอตรวจสอบ'
    return 'แนบแล้ว'
  }
  return 'ยังไม่แนบสลิป'
}

function canAttachSlipToTransaction(tx: WalletTransaction): boolean {
  const status = String(tx.status || '').trim().toLowerCase()
  const hasSlip = Boolean(tx.metadata?.submittedSlip?.imageUrl || tx.slipVerification?.slipData?.imageUrl)
  const sourceChannel = String(tx.metadata?.depositRouting?.sourceChannel || '').trim().toLowerCase()
  const isPayment = sourceChannel === 'payment' || Boolean(tx.metadata?.paymentGateway?.provider)
  return ['pending', 'processing', 'reserved', 'locked'].includes(status) && !hasSlip && !isPayment
}

function canReattachSlipToTransaction(tx: WalletTransaction): boolean {
  const status = String(tx.status || '').trim().toLowerCase()
  return ['pending', 'processing', 'reserved', 'locked'].includes(status)
}

function buildDepositDraftItem(draft: PendingDepositDraft, onContinue?: () => void): HistoryItem {
  const refId = draft.id
  const amount = Number(draft.amount) || 0
  const updatedAt = draft.updatedAt || draft.createdAt
  return {
    id: `draft:${draft.id}`,
    refId,
    title: draft.depositAccount?.bankName || 'รายการฝากค้าง',
    subtitle: formatDateTime(updatedAt),
    amountText: `${formatMoney(amount)} บาท`,
    amountClassName: 'c_tertiary font_md',
    leftIcon: 'pending',
    sortAt: Date.parse(updatedAt || '') || 0,
    queue: 'pending',
    actionLabel: onContinue ? 'ทำรายการต่อ' : undefined,
    onAction: onContinue,
    rows: [
      { label: 'สถานะ', value: getStatusLabel('draft') },
      { label: 'วิธีฝาก', value: draft.depositMethod },
      { label: 'จำนวนเงิน', value: `${formatMoney(amount)} บาท` },
      { label: 'บัญชีฝาก', value: draft.depositAccount?.accountNumber || '-' },
      { label: 'ชื่อบัญชี', value: draft.depositAccount?.accountName || '-' },
      { label: 'อัปเดตล่าสุด', value: formatDateTime(updatedAt) },
    ],
    searchText: [refId, draft.depositMethod, draft.depositAccount?.bankName, draft.depositAccount?.accountNumber].filter(Boolean).join(' ').toLowerCase(),
  }
}

function buildDepositTransactionItem(
  tx: WalletTransaction,
  onAttachSlip?: () => void,
  onViewSlip?: () => void
): HistoryItem | null {
  const status = String(tx.status || '').trim().toLowerCase()
  const queue = status === 'completed'
    ? 'completed'
    : ['pending', 'processing', 'reserved', 'locked'].includes(status)
      ? 'pending'
      : null

  if (!queue) return null

  const refId = tx.transactionId || tx._id
  const createdAt = tx.audit?.createdAt || tx.createdAt || tx.audit?.updatedAt || tx.updatedAt || ''
  const amount = Number(tx.amount) || 0
  const account = getDepositTargetAccount(tx)
  const hasSlip = Boolean(tx.metadata?.submittedSlip?.imageUrl || tx.slipVerification?.slipData?.imageUrl)
  const canAttachSlip = canAttachSlipToTransaction(tx)
  const canReattachSlip = canReattachSlipToTransaction(tx)

  return {
    id: `deposit:${tx._id}`,
    refId,
    title: account.bankName,
    subtitle: formatDateTime(createdAt),
    amountText: `${formatMoney(amount)} บาท`,
    amountClassName: queue === 'completed' ? 'c_success font_md' : 'c_tertiary font_md',
    leftIcon: queue === 'completed' ? 'success' : getLeftIconFromStatus(tx.status),
    sortAt: Date.parse(createdAt || '') || 0,
    queue,
    actionLabel: hasSlip && onViewSlip ? 'ดูสลิป' : canAttachSlip && onAttachSlip ? 'แนบสลิปภายหลัง' : undefined,
    onAction: hasSlip && onViewSlip ? onViewSlip : canAttachSlip && onAttachSlip ? onAttachSlip : undefined,
    secondaryActionLabel: hasSlip && canReattachSlip && onAttachSlip ? 'แนบใหม่' : undefined,
    onSecondaryAction: hasSlip && canReattachSlip && onAttachSlip ? onAttachSlip : undefined,
    rows: [
      { label: 'สถานะ', value: getStatusLabel(tx.status) },
      { label: 'ช่องทางฝาก', value: getDepositChannelLabel(tx) },
      { label: 'จำนวนเงิน', value: `${formatMoney(amount)} บาท` },
      { label: 'บัญชีปลายทาง', value: account.accountNumber },
      { label: 'ชื่อบัญชี', value: account.accountName },
      { label: 'สลิป', value: hasSlip ? `${getSlipStatusLabel(tx)} (กดดูสลิปได้)` : getSlipStatusLabel(tx) },
      {
        label: 'หมายเหตุ',
        value: translateDepositReviewReason(
          tx.metadata?.depositRouting?.reviewReason || tx.metadata?.notes || tx.details?.description || null,
          '-'
        ),
      },
      { label: 'เวลาทำรายการ', value: formatDateTime(createdAt) },
    ],
    searchText: [
      refId,
      account.bankName,
      account.accountName,
      account.accountNumber,
      tx.status,
      tx.metadata?.notes,
      tx.metadata?.depositRouting?.reviewReason,
      getDepositChannelLabel(tx),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase(),
  }
}

function isCreditTransaction(tx: WalletTransaction): boolean {
  const type = String(tx.type || '').toLowerCase()
  const amount = Number(tx.amount) || 0
  if (amount <= 0) return false
  return ['bonus', 'refund', 'adjustment', 'transfer_in', 'win', 'cancel'].includes(type)
}

function buildWalletTransactionItem(tx: WalletTransaction, mode: WalletHistoryMode): HistoryItem {
  const refId = tx.transactionId || tx._id
  const createdAt = tx.audit?.createdAt || tx.createdAt || tx.audit?.updatedAt || tx.updatedAt || ''
  const amount = Number(tx.amount) || 0
  const isPositive = amount >= 0
  const isWithdrawMode = mode === 'withdraw'
  const withdrawBankAccount = tx.details?.bankAccount?.accountNumber || '-'
  const withdrawBankAccountName = tx.details?.bankAccount?.accountName || '-'
  return {
    id: `${mode}:${tx._id}`,
    refId,
    title:
      isWithdrawMode
        ? tx.details?.bankAccount?.bankCode || 'ถอนเงิน'
        : getTransactionTypeLabel(tx.type),
    subtitle: formatDateTime(createdAt),
    amountText: getSignedAmountText(amount),
    amountClassName: `${isPositive ? 'c_success' : 'c_primary'} font_md`,
    leftIcon:
      mode === 'credit' && isPositive
        ? 'success'
        : getLeftIconFromStatus(tx.status),
    sortAt: Date.parse(createdAt || '') || 0,
    rows: isWithdrawMode
      ? [
          { label: 'สถานะ', value: getStatusLabel(tx.status) },
          { label: 'ประเภทรายการ', value: getTransactionTypeLabel(tx.type) },
          { label: 'จำนวนเงิน', value: getSignedAmountText(amount) },
          { label: 'ช่องทางถอน', value: getWithdrawChannelLabel(tx) },
          { label: 'เลขบัญชีรับเงิน', value: withdrawBankAccount },
          { label: 'ชื่อบัญชีรับเงิน', value: withdrawBankAccountName },
          { label: 'หมายเหตุ', value: getWithdrawNote(tx) },
          { label: 'เวลาทำรายการ', value: formatDateTime(createdAt) },
        ]
      : [
          { label: 'สถานะ', value: getStatusLabel(tx.status) },
          { label: 'ประเภทรายการ', value: getTransactionTypeLabel(tx.type) },
          { label: 'จำนวนเงิน', value: getSignedAmountText(amount) },
          { label: 'หมวดหมู่', value: tx.category || '-' },
          { label: 'บัญชีธนาคาร', value: tx.details?.bankAccount?.accountNumber || '-' },
          { label: 'ชื่อบัญชี', value: tx.details?.bankAccount?.accountName || '-' },
          { label: 'อ้างอิง', value: tx.details?.reference || refId },
          { label: 'หมายเหตุ', value: translateDepositReviewReason(tx.details?.description || null, '-') },
          { label: 'เวลาทำรายการ', value: formatDateTime(createdAt) },
        ],
    searchText: [
      refId,
      tx.details?.reference,
      tx.details?.description,
      tx.details?.bankAccount?.accountNumber,
      tx.details?.bankAccount?.accountName,
      tx.status,
      tx.type,
      tx.category,
      isWithdrawMode ? getWithdrawChannelLabel(tx) : '',
      isWithdrawMode ? getWithdrawNote(tx) : '',
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase(),
  }
}

function getPageTitle(mode: WalletHistoryMode): string {
  if (mode === 'deposit') return 'ประวัติการฝากเงิน'
  if (mode === 'withdraw') return 'ประวัติการถอนเงิน'
  return 'ประวัติการรับเครดิต'
}

function getEmptyMessage(mode: WalletHistoryMode, queue?: 'completed' | 'pending'): string {
  if (mode === 'deposit') {
    return queue === 'pending' ? 'ยังไม่มีรายการฝากที่รอดำเนินการ' : 'ยังไม่มีประวัติการฝากเงินที่สำเร็จ'
  }
  if (mode === 'withdraw') return 'ยังไม่มีประวัติการถอนเงิน'
  return 'ยังไม่มีประวัติการรับเครดิต'
}

function getDepositQueueButtonStyle(active: boolean): React.CSSProperties {
  if (active) {
    return {
      background: 'linear-gradient(180deg, rgba(37, 99, 235, 0.96) 0%, rgba(29, 78, 216, 0.96) 100%)',
      color: '#ffffff',
      boxShadow: '0 10px 24px rgba(37, 99, 235, 0.28)',
    }
  }

  return {
    background: 'rgba(255, 255, 255, 0.94)',
    color: '#0f172a',
    border: '1px solid rgba(148, 163, 184, 0.35)',
    boxShadow: '0 6px 18px rgba(15, 23, 42, 0.08)',
  }
}

interface HistoryWalletReportProps {
  mode: WalletHistoryMode
}

const HistoryWalletReport: React.FC<HistoryWalletReportProps> = ({ mode }) => {
  const navigate = useNavigate()
  const [items, setItems] = useState<HistoryItem[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [depositQueue, setDepositQueue] = useState<'completed' | 'pending'>('pending')

  const handlePreviewTransactionSlip = useCallback(async (transactionId: string) => {
    let previewUrl = ''
    try {
      previewUrl = await getTransactionSlipPreviewUrl(transactionId)
      await Swal.fire({
        title: 'ดูสลิปการโอน',
        imageUrl: previewUrl,
        imageAlt: 'slip payment',
        width: 720,
        confirmButtonText: 'ปิด',
        customClass: {
          popup: 'rounded-4',
        },
      })
    } catch (err) {
      await Swal.fire({
        icon: 'error',
        title: 'ดูสลิปไม่สำเร็จ',
        text: err instanceof Error ? err.message : 'ไม่สามารถโหลดรูปสลิปได้',
        confirmButtonText: 'ปิด',
      })
    } finally {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [])

  const loadHistory = useCallback(async () => {
    const user = authService.getCurrentUser() as { id?: string; _id?: string; tenantId?: string } | null
    const userId = user?.id || user?._id || ''
    const tenantId = user?.tenantId || ''

    if (!userId || !tenantId) {
      setItems([])
      setError('กรุณาเข้าสู่ระบบ')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      if (mode === 'deposit') {
        const drafts = pendingDepositDraftService.list(tenantId, userId)
        const transactionResponse = await getUserTransactions({
          type: 'deposit',
          limit: 100,
        })

        if (!transactionResponse.success) {
          throw new Error(transactionResponse.message || 'โหลดข้อมูลไม่สำเร็จ')
        }

        const depositTransactions = (transactionResponse.data?.transactions || [])
          .map((transaction) => buildDepositTransactionItem(
            transaction,
            () => navigate(`/topup/slip?transaction=${encodeURIComponent(transaction._id)}`),
            () => void handlePreviewTransactionSlip(transaction._id)
          ))
          .filter((item): item is HistoryItem => Boolean(item))

        const nextItems = [
          ...depositTransactions,
          ...drafts.map((draft) => buildDepositDraftItem(
            draft,
            () => navigate(
              draft.depositAccount
                ? `/topup/slip?draft=${encodeURIComponent(draft.id)}`
                : `/topup/confirm?draft=${encodeURIComponent(draft.id)}`
            )
          )),
        ].sort((a, b) => b.sortAt - a.sortAt)
        const hasPendingItems = nextItems.some((item) => item.queue === 'pending')
        const hasCompletedItems = nextItems.some((item) => item.queue === 'completed')
        setDepositQueue(hasPendingItems ? 'pending' : hasCompletedItems ? 'completed' : 'pending')
        setItems(nextItems)
        return
      }

      const transactionResponse = await getUserTransactions({
        ...(mode === 'withdraw' ? { type: 'withdraw' as const } : {}),
        limit: 100,
      })

      if (!transactionResponse.success) {
        throw new Error(transactionResponse.message || 'โหลดข้อมูลไม่สำเร็จ')
      }

      const transactions = transactionResponse.data?.transactions || []
      const filtered = mode === 'credit'
        ? transactions.filter(isCreditTransaction)
        : transactions.filter((transaction) => transaction.type === 'withdraw')

      setItems(
        filtered
          .map((transaction) => buildWalletTransactionItem(transaction, mode))
          .sort((a, b) => b.sortAt - a.sortAt)
      )
    } catch (err) {
      setItems([])
      setError(err instanceof Error ? err.message : 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [handlePreviewTransactionSlip, mode, navigate])

  useEffect(() => {
    loadHistory()
  }, [loadHistory])

  const filteredItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    const queueFiltered = mode === 'deposit'
      ? items.filter((item) => item.queue === depositQueue)
      : items
    if (!query) return queueFiltered
    return queueFiltered.filter((item) => item.searchText.includes(query))
  }, [depositQueue, items, mode, searchQuery])

  return (
    <>
      <div className="c_quinary text-center">
        <h3 className="c_tertiary font_md">{getPageTitle(mode)}</h3>
      </div>

      <div className="border_5 bg_secondary p-3 mt-3">
        {mode === 'deposit' && (
          <div className="d-flex gap-2 flex-wrap mb-3">
            <button
              type="button"
              className="border-0 border_30 px-3 py-2 font_md"
              style={getDepositQueueButtonStyle(depositQueue === 'completed')}
              onClick={() => setDepositQueue('completed')}
            >
              สำเร็จแล้ว
            </button>
            <button
              type="button"
              className="border-0 border_30 px-3 py-2 font_md"
              style={getDepositQueueButtonStyle(depositQueue === 'pending')}
              onClick={() => setDepositQueue('pending')}
            >
              รอดำเนินการ
            </button>
          </div>
        )}
        <div className="input-group border_5">
          <span className="input-group-text bg_secondary bdc_primary c_tertiary border_5">
            <i className="fas fa-search" />
          </span>
          <input
            type="text"
            className="form-control bg_secondary c_primary border_5"
            placeholder="ค้นหาเลขอ้างอิง, ชื่อบัญชี, สถานะ..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery.trim() && (
            <button
              type="button"
              className="btn bg_secondary bdc_primary c_tertiary border_5"
              onClick={() => setSearchQuery('')}
            >
              <i className="fas fa-times" />
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="border_5 bg_secondary py-5 overflow-hidden mt-3">
          <h4 className="text-center c_primary">กำลังโหลดข้อมูล...</h4>
        </div>
      ) : error ? (
        <div className="border_5 bg_secondary py-5 overflow-hidden mt-3">
          <h4 className="text-center c_primary">{error}</h4>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="border_5 bg_secondary py-5 overflow-hidden mt-3">
          <h4 className="text-center c_primary">{getEmptyMessage(mode, depositQueue)}</h4>
        </div>
      ) : (
        <div className="d-grid gap-2 mt-3">
          {filteredItems.map((item) => (
            <HistoryCollapseCard
              key={item.id}
              id={item.id}
              leftIcon={item.leftIcon}
              refId={item.refId}
              rows={item.rows}
              onCopyRef={() => navigator.clipboard.writeText(item.refId).catch(() => undefined)}
              actionLabel={item.actionLabel}
              onAction={item.onAction}
              secondaryActionLabel={item.secondaryActionLabel}
              onSecondaryAction={item.onSecondaryAction}
              header={
                <>
                  <h5 className="c_tertiary">{item.title}</h5>
                  <h5 className="c_tertiary">{item.subtitle}</h5>
                  <h5 className={item.amountClassName}>{item.amountText}</h5>
                </>
              }
            />
          ))}
        </div>
      )}
    </>
  )
}

export default HistoryWalletReport
